import type * as React from 'react';

import { tv } from 'tailwind-variants';

// Design System Button Styles https://www.figma.com/file/MKc5gEytDNyueOf4TXtxZs/Canny-UI-Kit?type=design&node-id=2089-7765&mode=design&t=tvEgLGQectc6UmrL-0
// Design System Button Styles https://www.figma.com/file/MKc5gEytDNyueOf4TXtxZs/Canny-UI-Kit?type=design&node-id=1200-2094&t=UtWrfBBDu2oV9LlV-0
export const buttonVariants = tv({
  slots: {
    base: [
      'inline-flex unstyled-button items-center justify-center w-fit gap-2',
      'text-transform-none box-border font-normal',
      'transition-all duration-300 ease-in-out',
      'hover:cursor-pointer shrink-0',
      '[&>svg]:shrink-0 text-base',
      'truncate overflow-hidden focus-visible:focus-ring',
    ],
  },
  variants: {
    variant: {
      contained: {
        base: 'border border-transparent',
      },
      outlined: {
        base: 'bg-background border border-gray-60',
      },
      plain: {
        base: 'bg-transparent border border-transparent',
      },
      ghost: {
        base: 'bg-transparent border border-transparent',
      },
      toggleOff: {
        base: 'bg-transparent border border-gray-60 text-gray-120',
      },
      toggleOn: {
        base: 'bg-indigo-20 border border-indigo-90 text-indigo-90',
      },
      unstyled: {
        base: '',
      },
    },
    fullWidth: {
      true: {
        base: 'w-full',
      },
      false: '',
    },
    color: {
      primary: '',
      error: '',
      success: '',
    },
    size: {
      small: {
        base: "h-7 px-2 rounded-sm leading-5 [&_svg:not([class*='size-'])]:!size-3.5",
      },
      default: {
        base: "h-8 px-3 rounded-sm leading-5 [&_svg:not([class*='size-'])]:!size-4",
      },
      large: {
        base: "h-11 px-4 rounded-lg text-lg leading-6 [&_svg:not([class*='size-'])]:!size-4.5",
      },
    },
    iconOnly: {
      true: {
        base: 'p-0 shrink-0 rounded-sm',
      },
      false: '',
    },
    disabled: {
      true: {
        base: 'opacity-50 text-muted-foreground bg-muted base-border',
      },
      false: '',
    },
    isLoading: {
      true: {
        base: 'hover:cursor-wait',
      },
      false: '',
    },
    isTinted: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'unstyled',
      class: {
        base: 'h-auto p-0 bg-transparent border-none',
      },
    },
    {
      iconOnly: true,
      size: 'small',
      class: {
        base: 'size-7',
      },
    },
    {
      iconOnly: true,
      size: 'default',
      class: {
        base: 'size-8',
      },
    },
    {
      iconOnly: true,
      size: 'large',
      class: {
        base: 'size-11',
      },
    },
    {
      variant: 'contained',
      color: 'primary',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'bg-indigo-90 text-white border-indigo-90',
          'hover:bg-indigo-100 focus-visible:bg-indigo-100',
          'active:bg-indigo-110',
        ],
      },
    },
    {
      variant: 'contained',
      color: 'error',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'bg-red-90 text-white border-red-90',
          'hover:bg-red-100 focus-visible:bg-red-100',
          'active:bg-red-110',
        ],
      },
    },
    {
      variant: 'contained',
      color: 'success',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'bg-green-90 text-white border-green-90',
          'hover:bg-green-100 focus-visible:bg-green-100',
          'active:bg-green-110',
        ],
      },
    },
    {
      variant: 'outlined',
      color: 'primary',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'text-gray-120 border-input',
          'hover:bg-gray-10 hover:text-black hover:border-input-focus',
          'focus-visible:bg-gray-10 focus-visible:text-black focus-visible:border-input-focus',
          'active:bg-gray-20 active:border-gray-80',
        ],
      },
    },
    {
      variant: 'outlined',
      color: 'error',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'text-red-110 border-red-90',
          'hover:bg-red-40 hover:text-red-90 hover:border-red-90',
          'focus-visible:bg-red-40 focus-visible:text-red-90 focus-visible:border-red-90',
          'active:bg-red-50',
        ],
      },
    },
    {
      variant: 'outlined',
      color: 'success',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'text-green-110 border-green-90',
          'hover:bg-green-40',
          'focus-visible:bg-green-40',
          'active:bg-green-50',
        ],
      },
    },
    {
      iconOnly: true,
      variant: 'outlined',
      color: 'error',
      disabled: false,
      class: {
        base: ['border-gray-60'],
      },
    },
    {
      variant: 'ghost',
      color: 'primary',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'text-gray-120',
          'hover:bg-indigo-40 hover:border-indigo-40 hover:text-indigo-90',
          'focus-visible:bg-indigo-40 focus-visible:border-indigo-40 focus-visible:text-indigo-90',
          'active:bg-indigo-50 active:border-indigo-50',
        ],
      },
    },
    {
      variant: 'ghost',
      color: 'error',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'text-red-90',
          'hover:bg-red-40 hover:border-red-40 hover:text-red-90',
          'focus-visible:bg-red-40 focus-visible:border-red-40 focus-visible:text-red-90',
          'active:bg-red-50 active:border-red-50',
        ],
      },
    },
    {
      variant: 'ghost',
      color: 'success',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'text-green-90',
          'hover:bg-green-40 hover:border-green-40 hover:text-green-90',
          'focus-visible:bg-green-40 focus-visible:border-green-40 focus-visible:text-green-90',
          'active:bg-green-50 active:border-green-50',
        ],
      },
    },
    {
      variant: 'plain',
      iconOnly: false,
      class: {
        base: 'px-0',
      },
    },
    {
      variant: 'plain',
      color: 'primary',
      disabled: false,
      class: {
        base: [
          'text-indigo-90',
          'hover:text-indigo-100 focus-visible:text-indigo-100',
          'active:text-indigo-110',
        ],
      },
    },
    {
      variant: 'plain',
      color: 'error',
      disabled: false,
      class: {
        base: [
          'text-red-90',
          'hover:text-red-100 focus-visible:text-red-100',
          'active:text-red-110',
        ],
      },
    },
    {
      variant: 'plain',
      color: 'success',
      disabled: false,
      class: {
        base: [
          'text-green-90',
          'hover:text-green-100 focus-visible:text-green-100',
          'active:text-green-110',
        ],
      },
    },
    {
      iconOnly: true,
      variant: 'plain',
      color: 'primary',
      disabled: false,
      class: {
        base: [
          'text-gray-110 hover:text-gray-110 focus-visible:text-gray-110',
          'hover:bg-gray-20 focus-visible:bg-gray-10 focus-visible:text-gray-120',
          'active:bg-gray-30 active:text-gray-120',
        ],
      },
    },
    {
      iconOnly: true,
      variant: 'plain',
      color: 'error',
      disabled: false,
      class: {
        base: [
          'text-red-90',
          'hover:bg-red-30 focus-visible:bg-red-30 focus-visible:border-red-90',
          'active:bg-red-40 active:border-red-40',
        ],
      },
    },
    {
      disabled: true,
      variant: 'outlined',
      class: {
        base: 'border-gray-60',
      },
    },
    {
      isTinted: true,
      disabled: false,
      class: {
        base: [
          'bg-[var(--company-color)] border-[var(--company-color)]',
          'hover:opacity-80 active:opacity-70',
        ],
      },
    },
    {
      variant: 'toggleOff',
      color: 'primary',
      disabled: false,
      class: {
        base: ['hover:bg-indigo-10 focus-visible:bg-indigo-10 active:bg-indigo-10'],
      },
    },
    {
      variant: 'toggleOff',
      color: 'error',
      disabled: false,
      class: {
        base: [
          'hover:bg-red-40 hover:border-red-90 hover:text-red-90',
          'focus-visible:bg-red-40 focus-visible:border-red-90 focus-visible:text-red-90',
          'active:bg-red-50',
        ],
      },
    },
    {
      variant: 'toggleOff',
      color: 'success',
      disabled: false,
      class: {
        base: [
          'hover:bg-green-40 hover:border-green-90 hover:text-green-90',
          'focus-visible:bg-green-40 focus-visible:border-green-90 focus-visible:text-green-90',
          'active:bg-green-50',
        ],
      },
    },
    {
      variant: 'toggleOn',
      color: 'primary',
      disabled: false,
      class: {
        base: [
          'hover:bg-indigo-30 hover:text-indigo-100',
          'focus-visible:bg-indigo-30 focus-visible:text-indigo-100',
          'active:bg-indigo-30',
        ],
      },
    },
    {
      variant: 'toggleOn',
      color: 'error',
      disabled: false,
      class: {
        base: [
          'hover:bg-red-40 hover:border-red-90 hover:text-red-90',
          'focus-visible:bg-red-40 focus-visible:border-red-90 focus-visible:text-red-90',
          'active:bg-red-50',
        ],
      },
    },
    {
      variant: 'toggleOn',
      color: 'success',
      disabled: false,
      class: {
        base: [
          'hover:bg-green-40 hover:border-green-90 hover:text-green-90',
          'focus-visible:bg-green-40 focus-visible:border-green-90 focus-visible:text-green-90',
          'active:bg-green-50',
        ],
      },
    },
    {
      variant: 'plain',
      disabled: true,
      class: {
        base: 'border-transparent bg-transparent',
      },
    },
  ],
  defaultVariants: {
    variant: 'contained',
    color: 'primary',
    size: 'default',
    disabled: false,
    loading: false,
    isTinted: false,
    iconOnly: false,
  },
});

export const getStyleWithTint = (
  style: React.CSSProperties | undefined,
  tint: string | null | undefined,
): React.CSSProperties | undefined => {
  if (!tint) {
    return style;
  }

  return {
    ...style,
    '--company-color': tint,
  } as React.CSSProperties;
};
